import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!token) { setError("Missing reset token"); return; }
        if (password !== confirm) { setError("Passwords do not match"); return; }
        if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
        setBusy(true); setError("");
        try {
            await api.post("/auth/reset-password", { token, password });
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 1500);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-ink-50">
            <div className="hidden lg:flex flex-col justify-between p-12 bg-haevn-700 text-white grain relative overflow-hidden">
                <Logo tone="dark" className="h-7" showSub />
                <div className="max-w-md">
                    <div className="text-[10px] uppercase tracking-[0.25em] opacity-70 font-mono mb-4">Account recovery</div>
                    <h1 className="font-editorial text-4xl xl:text-5xl leading-[1.05]">A fresh start.</h1>
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-50 font-mono">Authorized personnel only</div>
            </div>
            <div className="flex flex-col justify-center p-8 md:p-16">
                <div className="w-full max-w-sm mx-auto">
                    <div className="lg:hidden mb-8"><Logo tone="light" className="h-7" showSub /></div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Reset password</div>
                    <h2 className="font-editorial text-3xl text-ink-900 mb-8">Choose a new password.</h2>
                    {done ? (
                        <div className="text-sm text-ink-700" data-testid="reset-success">Password updated. Redirecting to sign in…</div>
                    ) : (
                        <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-form">
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">New password</label>
                                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="reset-password-input"
                                    className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700" />
                            </div>
                            <div>
                                <label className="block text-[11px] uppercase tracking-[0.15em] text-ink-500 font-mono mb-1.5">Confirm</label>
                                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} data-testid="reset-confirm-input"
                                    className="w-full bg-white border border-ink-300 rounded-sm px-4 py-2.5 text-sm focus:outline-none focus:border-haevn-700" />
                            </div>
                            {error && <div className="text-xs text-clay-500 bg-clay-50 px-3 py-2 rounded-sm" data-testid="reset-error">{error}</div>}
                            <button type="submit" disabled={busy} data-testid="reset-submit-btn"
                                className="w-full bg-haevn-700 text-white py-2.5 rounded-sm hover:bg-haevn-900 transition-colors font-medium text-sm disabled:opacity-50">
                                {busy ? "Saving…" : "Update password"}
                            </button>
                        </form>
                    )}
                    <div className="mt-8 pt-6 border-t border-ink-200 text-center text-sm">
                        <Link to="/login" className="text-haevn-700 underline underline-offset-4 font-medium">Back to sign in</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
